/**
 * Duration for video fits better if there will be type specific media item.. however, is it ok to implement
 * an additional one of field called details?
 * A media item in the Enterprise Public Media system. Items can be organized into categories and shared with child accounts by setting their `publishStatus` to `PUBLISHED`.
 */
export interface EnterpriseMediaItem {
  /**
   * Item ID.
   * @maxLength 100
   * @readonly
   */
  id?: string;
  /**
   * Media type of the item.
   * @readonly
   */
  mediaType?: MediaTypeWithLiterals;
  /**
   * Size of the uploaded file in bytes.
   * @readonly
   * @maxLength 1000
   */
  sizeInBytes?: string | null;
  /**
   * Title of the item. Used for searching and displaying the item.
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * Item assets including URLs for different renditions and formats.
   * @readonly
   */
  assets?: ItemAssets;
  /**
   * User-facing tags describing the item. Indexed for text search; supports partial matching.
   * @maxSize 100
   * @maxLength 100
   */
  displayTags?: string[] | null;
  /**
   * Tags for custom categorization by client applications. Searchable with exact match only.
   * @maxSize 100
   * @maxLength 100
   */
  internalTags?: string[] | null;
  /**
   * Read-only tags generated by the system. Includes information about the uploader (via `uploaderSystemTag`) and external source references (via `uploaderInfoSystemTag`). Use these tags to filter items by their source.
   * @maxSize 100
   * @maxLength 100
   * @readonly
   */
  systemTags?: string[] | null;
  /**
   * IDs of categories the item belongs to. To modify category assignments, call [Link Item To Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/link-item-to-categories), [Unlink Item From Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/unlink-item-from-categories), or [Overwrite Item Categories](https://dev.wix.com/docs/api-reference/assets/media/enterprise-media/enterprise-item/overwrite-item-categories).
   * @maxSize 100
   * @maxLength 100
   * @readonly
   */
  parentCategoryIds?: string[] | null;
  /** Publish status of the item. When `PUBLISHED`, the item is visible in child accounts' media managers. When `UNPUBLISHED`, the item is only visible in the account dashboard. */
  publishStatus?: PublishStatusWithLiterals;
  /**
   * Date and time the item was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the item was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Internal ID used to reference the item within Wix media systems.
   * @maxLength 100
   * @readonly
   */
  internalId?: string | null;
}

export enum MediaType {
  /** Mixed media types. Used for categories that contain multiple types of media. Individual items should use specific media types (IMAGE, VIDEO, etc.) rather than MIXED. */
  MIXED = 'MIXED',
  /** Image file. */
  IMAGE = 'IMAGE',
  /** Video file. */
  VIDEO = 'VIDEO',
  /** Audio file. */
  AUDIO = 'AUDIO',
  /** Document file. */
  DOCUMENT = 'DOCUMENT',
  /** Vector image file. */
  VECTOR = 'VECTOR',
  /** Archive file. Supported formats: ZIP, RAR, TAR, TAR.GZ, GZ, GZIP, JAR, 7Z, FGZ, and WEBARCHIVE. Maximum file size: 4000MB. */
  ARCHIVE = 'ARCHIVE',
  /** 3D model file. */
  MODEL3D = 'MODEL3D',
}

/** @enumType */
export type MediaTypeWithLiterals =
  | MediaType
  | 'MIXED'
  | 'IMAGE'
  | 'VIDEO'
  | 'AUDIO'
  | 'DOCUMENT'
  | 'VECTOR'
  | 'ARCHIVE'
  | 'MODEL3D';

/** Contains media-specific asset information based on the item's `mediaType`. Only one asset field is populated per item. During initial upload or import, this field may be empty until processing completes. */
export interface ItemAssets extends ItemAssetsAssetsOneOf {
  /** Image assets and metadata. */
  image?: Image;
  /** Video assets and metadata. */
  video?: VideoV2;
  /** Vector image assets and metadata. */
  vector?: Image;
  /** Audio assets and metadata. */
  audio?: Audio;
  /** Document assets and metadata. */
  document?: Document;
  /** Archive file metadata. */
  archive?: Archive;
  /** 3D model metadata. */
  model3d?: Model3D;
}

/** @oneof */
export interface ItemAssetsAssetsOneOf {
  /** Image assets and metadata. */
  image?: Image;
  /** Video assets and metadata. */
  video?: VideoV2;
  /** Vector image assets and metadata. */
  vector?: Image;
  /** Audio assets and metadata. */
  audio?: Audio;
  /** Document assets and metadata. */
  document?: Document;
  /** Archive file metadata. */
  archive?: Archive;
  /** 3D model metadata. */
  model3d?: Model3D;
}

export interface Image {
  /** WixMedia image ID. */
  id?: string;
  /** Image URL. */
  url?: string;
  /**
   * Original image height.
   * @readonly
   */
  height?: number;
  /**
   * Original image width.
   * @readonly
   */
  width?: number;
  /** Image alt text. */
  altText?: string | null;
  /**
   * Image filename.
   * @readonly
   */
  filename?: string | null;
}

export interface VideoV2 {
  /** WixMedia ID. */
  id?: string;
  /**
   * Available resolutions for the video, starting with the optimal resolution.
   * @readonly
   * @maxSize 100
   */
  resolutions?: VideoResolution[];
  /**
   * Video filename.
   * @readonly
   */
  filename?: string | null;
}

export interface VideoResolution {
  /** Video URL. */
  url?: string;
  /** Video height. */
  height?: number;
  /** Video width. */
  width?: number;
  /**
   * Video format
   * Possible values: ['144p.mp4' '144p.webm' '240p.mp4' '240p.webm' '360p.mp4' '360p.webm' '480p.mp4' '480p.webm'
   * '720p.mp4' '720p.webm' '1080p.mp4' '1080p.webm' ]
   */
  format?: string;
}

export interface Audio {
  /** WixMedia ID. */
  id?: string;
  /** Audio URL. */
  url?: string;
  /**
   * Audio filename.
   * @readonly
   */
  filename?: string | null;
}

export interface Document {
  /** WixMedia ID. */
  id?: string;
  /** Document URL. */
  url?: string;
  /** Document filename. */
  filename?: string | null;
}

export interface Archive {
  /** WixMedia ID. */
  id?: string;
  /** Archive URL. */
  url?: string;
  /**
   * Archive URL expiration date (when relevant).
   * @readonly
   */
  urlExpirationDate?: Date | null;
  /**
   * Archive size in bytes.
   * @format DECIMAL_VALUE
   */
  sizeInBytes?: string | null;
  /** Archive filename. */
  filename?: string | null;
}

export interface Model3D {
  /** WixMedia 3D ID. */
  id?: string;
  /** 3D URL. */
  url?: string;
  /** 3D thumbnail Image */
  thumbnail?: Image;
  /** 3D alt text. */
  altText?: string | null;
  /**
   * 3D URL expiration date (when relevant).
   * @readonly
   */
  urlExpirationDate?: Date | null;
  /**
   * 3D filename.
   * @readonly
   */
  filename?: string | null;
  /**
   * 3D size in bytes.
   * @readonly
   * @format DECIMAL_VALUE
   */
  sizeInBytes?: string | null;
}

export enum PublishStatus {
  /** Unknown publish status. */
  UNDEFINED = 'UNDEFINED',
  /** Not published. Visible only in the account dashboard. */
  UNPUBLISHED = 'UNPUBLISHED',
  /** Published. Visible in child accounts' media managers. */
  PUBLISHED = 'PUBLISHED',
}

/** @enumType */
export type PublishStatusWithLiterals =
  | PublishStatus
  | 'UNDEFINED'
  | 'UNPUBLISHED'
  | 'PUBLISHED';

/** Triggered when an item's category links change. Also triggers an UPDATED domain event. */
export interface ItemCategoriesChanged {
  /**
   * Current category IDs linked to the item.
   * @maxLength 100
   * @maxSize 100
   */
  itemCategoryIds?: string[] | null;
  /**
   * IDs of categories that were unlinked from the item.
   * @maxLength 100
   * @maxSize 20
   */
  unlinkedCategoryIds?: string[] | null;
  /**
   * IDs of categories that were linked to the item.
   * @maxLength 100
   * @maxSize 20
   */
  linkedCategoryIds?: string[] | null;
  /** Updated item. */
  entity?: EnterpriseMediaItem;
}

export interface ItemUploadCallbackRequest {
  /**
   * ID of the created item.
   * @maxLength 100
   */
  itemId?: string;
  /**
   * Callback token provided to the upload endpoint.
   * @maxLength 10000
   */
  callbackToken?: string;
}

export interface ItemUploadCallbackResponse {
  /** Message describing the callback result. */
  message?: string | null;
}

export interface GenerateFileUploadUrlRequest {
  /**
   * Original file name including the extension. Used as the initial display name for the created item.
   * @maxLength 1000
   */
  fileName?: string;
  /**
   * MIME type of the file. Used to identify the media type.
   * @maxLength 1000
   */
  contentType?: string;
  /** File size in bytes. */
  sizeInBytes?: number;
  /**
   * IDs of additional categories to link the created item to. The item is automatically linked to the root account category and to any categories specified here.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
}

export interface GenerateFileUploadUrlResponse {
  /**
   * Upload URL where the file should be uploaded.
   * @maxLength 10000
   */
  uploadUrl?: string;
}

export interface ImportFileRequest {
  /**
   * URL of the file to import.
   * @format WEB_URL
   */
  url: string;
  /**
   * Original file name including the extension. Used as the initial display name for the created item.
   * @maxLength 1000
   */
  fileName?: string | null;
  /**
   * MIME type of the file. Used to identify the media type.
   * @maxLength 1000
   */
  contentType?: string | null;
  /** File size in bytes. */
  sizeInBytes?: number | null;
  /**
   * IDs of categories to link the created item to. The item will be automatically linked to the root account category.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
  /** Media type of the file. */
  mediaType?: MediaTypeWithLiterals;
  /**
   * Identifier of the external app or system that imported the file. Used to filter items by uploader. For example, if multiple apps upload to the same account, each app can use its own identifier to retrieve only items it created. Stored in `systemTags` prefixed by `_external_uploader:`.
   * @maxLength 100
   */
  uploaderSystemTag?: string | null;
  /**
   * Additional metadata about the external source. Use this to store identifiers of related entities in external systems, such as external item IDs, source URLs, or reference numbers. This complements `uploaderSystemTag`, which identifies the importing app itself. Stored in `systemTags` prefixed by `_external_uploader_info:`.
   * @maxLength 1000
   */
  uploaderInfoSystemTag?: string | null;
}

export interface ImportFileResponse {
  /** Partial item information. At this stage, only `internalId` is populated. All other required fields contain placeholder values. */
  item?: EnterpriseMediaItem;
}

export interface BulkImportFilesRequest {
  /**
   * List of files to import.
   * @minSize 1
   * @maxSize 100
   */
  importFileRequests: ImportFileRequest[];
  /**
   * Whether to include the imported item in the response. Set to `false` to exclude the item from the returned object.
   *
   * Default: `true`
   */
  returnEntity?: boolean | null;
  /**
   * IDs of categories to link all imported items to, in addition to any categories specified in individual `importFileRequests`. Items are also automatically linked to the root account category.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[] | null;
}

export interface BulkImportFilesResponse {
  /** Results for each individual file import operation. */
  results?: BulkImportFilesResult[];
  /** Metadata about the bulk operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkImportFilesResult {
  /** Metadata about the import operation result. */
  itemMetadata?: ItemMetadata;
  /** Imported item. Returned only if the operation was successful and `returnEntity` is not set to `false`. */
  item?: EnterpriseMediaItem;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface SearchItemsRequest {
  /** Search query for text-based matching across item `title`, `displayTags`, and `internalTags` fields. Partial matching is supported for `title` and `displayTags`. `internalTags` require a full exact match. All filters support equality only. Each query must include a `categoryId` filter. */
  query?: Search;
}

export interface Search extends SearchPagingMethodOneOf {
  /** Pointer to page of results using offset. Can not be used together with 'cursor_paging' */
  paging?: Paging;
  /** A filter object. See documentation [here](https://bo.wix.com/wix-docs/rnd/platformization-guidelines/api-query-language#platformization-guidelines_api-query-language_defining-in-protobuf) */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the form [{"fieldName":"sortField1"},{"fieldName":"sortField2","direction":"DESC"}]
   * @maxSize 50
   */
  sort?: Sorting[];
  /** free text to match in searchable fields */
  search?: SearchDetails;
}

/** @oneof */
export interface SearchPagingMethodOneOf {
  /** Pointer to page of results using offset. Can not be used together with 'cursor_paging' */
  paging?: Paging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface SearchDetails {
  /** search term or expression */
  expression?: string | null;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface SearchItemsResponse {
  /**
   * List of items matching the search query.
   * @maxSize 200
   */
  items?: EnterpriseMediaItem[];
  /** Paging information for the search results. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
}

export interface QueryItemsRequest {
  /** Query parameters to filter, sort, and page items. All filters support equality only. Each query must include a `categoryId` filter. Supported sort fields: `createdDate`, `updatedDate`, `title`, `sizeInBytes`. */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
}

export interface QueryItemsResponse {
  /**
   * List of items matching the query.
   * @maxSize 200
   */
  items?: EnterpriseMediaItem[];
  /** Paging information for the query results. */
  pagingMetadata?: PagingMetadataV2;
}

export interface UpdateItemRequest {
  /** Item to update. */
  item: EnterpriseMediaItem;
}

export interface UpdateItemResponse {
  /** Updated item. */
  item?: EnterpriseMediaItem;
}

/** Triggered when an item's `publishStatus` changes. Also triggers an UPDATED domain event. */
export interface PublishStatusChanged {
  /** New publish status of the item. */
  publishStatus?: PublishStatusWithLiterals;
  /** Updated item. */
  entity?: EnterpriseMediaItem;
}

export interface BulkUpdateItemRequest {
  /**
   * List of items to update. Each item must include an ID.
   * @maxSize 20
   * @minSize 1
   */
  updateItemRequests: UpdateItemRequest[];
  /** Whether to return the updated items in the response. */
  returnEntity?: boolean;
}

export interface BulkUpdateItemResponse {
  /** Results for each individual item update operation. */
  results?: BulkItemUpdateResult[];
  /** Metadata about the bulk operation. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkItemUpdateResult {
  /** Metadata about the update operation result. */
  itemMetadata?: ItemMetadata;
  /** Updated item. Returned only if the operation was successful and `returnEntity` was set to `true`. */
  item?: EnterpriseMediaItem;
}

export interface GetItemRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
}

export interface GetItemResponse {
  /** Retrieved item. */
  item?: EnterpriseMediaItem;
}

export interface LinkItemToCategoriesRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
  /**
   * IDs of categories to link to the item.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

export interface LinkItemToCategoriesResponse {
  /**
   * IDs of categories that were successfully linked to the item.
   * @maxLength 100
   * @maxSize 20
   */
  linkedCategoryIds?: string[] | null;
}

export interface UnlinkItemFromCategoriesRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
  /**
   * IDs of categories to unlink from the item.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

export interface UnlinkItemFromCategoriesResponse {
  /**
   * IDs of categories that were successfully unlinked from the item.
   * @maxLength 100
   * @maxSize 20
   */
  unlinkedCategoryIds?: string[] | null;
}

export interface OverwriteItemCategoriesRequest {
  /**
   * Item ID.
   * @maxLength 100
   */
  itemId: string;
  /**
   * IDs of categories the item will be linked to after this operation. All existing category links will be replaced.
   * @maxLength 100
   * @maxSize 20
   */
  categoryIds?: string[];
}

export interface OverwriteItemCategoriesResponse {
  /**
   * IDs of categories that were linked to the item.
   * @maxLength 100
   * @maxSize 20
   */
  linkedCategoryIds?: string[] | null;
  /**
   * IDs of categories that were unlinked from the item.
   * @maxLength 100
   * @maxSize 20
   */
  unlinkedCategoryIds?: string[] | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
